import { createBrowserRouter, Navigate } from 'react-router';
import { lazy, Suspense } from 'react';
import { AppShell } from '@/features/shell/layouts/AppShell';
import { LoginPage } from '@/features/auth/login/LoginPage';
import { ChangePasswordPage } from '@/features/auth/changePassword/ChangePasswordPage';
import { AnonymousOnly, RequireAuth, RequireSysRole, RequireWebLicense } from '@/features/auth/guards';
import { RouteErrorPage } from '@/app/RouteErrorPage';
import { ScreenLoader } from '@/shared/ui/ScreenLoader';
import { useAuthStore } from '@/entities/session/model/authStore';
import { lastViewMode } from '@/features/node/model/viewMode';

/**
 * Node (el terminali) görünümü kendi kabuğu: masaüstü kabuğunun yanında değil, onun
 * YERİNE çiziliyor ve web lisansı istemiyor — yalnızca node lisanslı kullanıcının tek
 * gidebileceği yer burası, bu yüzden RequireWebLicense'ın dışında duruyor.
 *
 * Ayrı paket: masaüstünden node'a hiç geçmeyen kullanıcı bu kodu indirmez.
 */
const NodeApp = lazy(() => import('@/features/node/NodeApp'));

/**
 * OctoLab (nesne tasarımcısı) kendi tam ekran sayfası: kabuğun sekme çubuğu ve menüsü
 * tasarımcıda yer kaplıyor, zaten ayrı pencerede açılıyor. Ayrı paket — tasarımcıyı hiç
 * açmayan kullanıcı bu kodu indirmez.
 */
const LabPage = lazy(() => import('@/features/octolab/LabPage'));
/** SQL editör (SSMS benzeri) — Monaco ile birlikte ayrı paket; yalnız açılınca iner. */
const SqlEditorPage = lazy(() => import('@/features/sqleditor/SqlEditorPage'));

/**
 * Kök adres. Uygulama (PWA kısayolu, yer imi) her zaman "/" ile açılıyor; kullanıcı en
 * son el terminali görünümündeyse oradan devam etmeli — yoksa her açılışta masaüstü
 * kabuğuyla karşılaşırdı. Web'de bu kararı çereze bakan sunucu veriyor; burada cihazın
 * kendi hafızası (bkz. viewMode).
 *
 * Yalnızca KÖK adres: /s/{id} gibi derin linkler dokunulmadan geçer, kullanıcı belirli
 * bir ekranı açmak istiyor.
 */
function RootView() {
  const user = useAuthStore((s) => s.user);
  if (user && lastViewMode() === 'node') return <Navigate to="/node" replace />;
  return <AppShell />;
}

export const router = createBrowserRouter([
  {
    element: <AnonymousOnly />,
    errorElement: <RouteErrorPage />,
    children: [{ path: '/login', element: <LoginPage /> }],
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/change-password', element: <ChangePasswordPage /> },
      {
        path: '/node/*',
        element: (
          <Suspense
            fallback={
              <ScreenLoader kind="app" className="h-dvh" />
            }
          >
            <NodeApp />
          </Suspense>
        ),
      },
      {
        // Sys rol gerektiren geliştirici aracı.
        element: <RequireSysRole />,
        children: [
          {
            path: '/octo-lab/:objectId?',
            element: (
              <Suspense
                fallback={
                  <ScreenLoader kind="designer" className="h-dvh" />
                }
              >
                <LabPage />
              </Suspense>
            ),
          },
          {
            // Erişim düzeyi (Renium tam / diğer sys adminleri kısıtlı) sunucuda belirlenir.
            path: '/sql-editor',
            element: (
              <Suspense fallback={<ScreenLoader kind="designer" className="h-dvh" />}>
                <SqlEditorPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        element: <RequireWebLicense />,
        children: [
          { path: '/', element: <RootView /> },
          { path: '/s/:screenId', element: <AppShell /> },
          // Bildirim bağlantısı: ekranı kayda süzülmüş açar (bkz. features/shell/lib/deepLinks).
          { path: '/go', element: <AppShell /> },
          { path: '/settings', element: <AppShell /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
